/**
 * MOD-05 Batch 1: Leave Management REST API Client
 */
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || '';

export const leaveApi = {
  // Tenant Configuration
  getConfig: async () => {
    const res = await axios.get(`${BASE_URL}/api/leaves/config/`, { withCredentials: true });
    return res.data;
  },

  updateConfig: async (configData) => {
    const res = await axios.put(`${BASE_URL}/api/leaves/config/`, configData, { withCredentials: true });
    return res.data;
  },

  // Leave Types
  getTypes: async () => {
    const res = await axios.get(`${BASE_URL}/api/leaves/types/`, { withCredentials: true });
    return res.data;
  },

  createType: async (typeData) => {
    const res = await axios.post(`${BASE_URL}/api/leaves/types/`, typeData, { withCredentials: true });
    return res.data;
  },

  updateType: async (id, typeData) => {
    const res = await axios.put(`${BASE_URL}/api/leaves/types/${id}/`, typeData, { withCredentials: true });
    return res.data;
  },

  // Leave Policies
  getPolicies: async () => {
    const res = await axios.get(`${BASE_URL}/api/leaves/policies/`, { withCredentials: true });
    return res.data;
  },

  createPolicy: async (policyData) => {
    const res = await axios.post(`${BASE_URL}/api/leaves/policies/`, policyData, { withCredentials: true });
    return res.data;
  },

  updatePolicy: async (id, policyData) => {
    const res = await axios.put(`${BASE_URL}/api/leaves/policies/${id}/`, policyData, { withCredentials: true });
    return res.data;
  },

  // Reference Template Seeder
  seedTemplates: async () => {
    const res = await axios.post(`${BASE_URL}/api/leaves/seed-templates/`, {}, { withCredentials: true });
    return res.data;
  },

  // Balances & Ledger
  getBalances: async (params = '') => {
    const query = params ? `?${params}` : '';
    const res = await axios.get(`${BASE_URL}/api/leaves/balances/${query}`, { withCredentials: true });
    return res.data;
  },

  getLedger: async (params = '') => {
    const query = params ? `?${params}` : '';
    const res = await axios.get(`${BASE_URL}/api/leaves/ledger/${query}`, { withCredentials: true });
    return res.data;
  },

  adjustBalance: async (adjustmentData) => {
    const res = await axios.post(`${BASE_URL}/api/leaves/adjust-balance/`, adjustmentData, { withCredentials: true });
    return res.data;
  },

  // MOD-05 Batch 2: Gazetted Holidays
  getHolidays: async (params = '') => {
    const query = params ? `?${params}` : '';
    const res = await axios.get(`${BASE_URL}/api/leaves/holidays/${query}`, { withCredentials: true });
    return res.data;
  },

  createHoliday: async (holidayData) => {
    const res = await axios.post(`${BASE_URL}/api/leaves/holidays/`, holidayData, { withCredentials: true });
    return res.data;
  },

  deleteHoliday: async (id) => {
    const res = await axios.delete(`${BASE_URL}/api/leaves/holidays/${id}/`, { withCredentials: true });
    return res.data;
  },

  // MOD-05 Batch 2: Manager Delegations
  getDelegations: async (params = '') => {
    const query = params ? `?${params}` : '';
    const res = await axios.get(`${BASE_URL}/api/leaves/delegations/${query}`, { withCredentials: true });
    return res.data;
  },

  createDelegation: async (delegationData) => {
    const res = await axios.post(`${BASE_URL}/api/leaves/delegations/`, delegationData, { withCredentials: true });
    return res.data;
  },

  revokeDelegation: async (id) => {
    const res = await axios.post(`${BASE_URL}/api/leaves/delegations/${id}/revoke/`, {}, { withCredentials: true });
    return res.data;
  },

  // MOD-05 Batch 2: Modern Leave Requests & Approvals
  getModernRequests: async (params = '') => {
    const query = params ? `?${params}` : '';
    const res = await axios.get(`${BASE_URL}/api/leaves/requests/${query}`, { withCredentials: true });
    return res.data;
  },

  submitModernRequest: async (formData, idempotencyKey) => {
    const headers = {
      withCredentials: true,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
      },
    };
    const res = await axios.post(`${BASE_URL}/api/leaves/requests/`, formData, headers);
    return res.data;
  },

  getPendingApprovals: async (params = '') => {
    const query = params ? `?${params}` : '';
    const res = await axios.get(`${BASE_URL}/api/leaves/approvals/${query}`, { withCredentials: true });
    return res.data;
  },

  decideApprovalStep: async (stepId, decisionData) => {
    const res = await axios.post(`${BASE_URL}/api/leaves/approvals/${stepId}/decide/`, decisionData, { withCredentials: true });
    return res.data;
  },

  // MOD-05 Batch 2: Escalations & Stale Evaluations
  getEscalations: async (params = '') => {
    const query = params ? `?${params}` : '';
    const res = await axios.get(`${BASE_URL}/api/leaves/escalations/${query}`, { withCredentials: true });
    return res.data;
  },

  triggerStaleEvaluation: async () => {
    const res = await axios.post(`${BASE_URL}/api/leaves/evaluate-stale/`, {}, { withCredentials: true });
    return res.data;
  },

  // MOD-05 Batch 3: Multi-State Holidays, Restricted Holidays, Accrual & Sandwich
  getApplicableHolidays: async (year = '') => {
    const query = year ? `?year=${year}` : '';
    const res = await axios.get(`${BASE_URL}/api/leaves/holidays/applicable/${query}`, { withCredentials: true });
    return res.data;
  },

  getRestrictedHolidays: async (year = '') => {
    const query = year ? `?year=${year}` : '';
    const res = await axios.get(`${BASE_URL}/api/leaves/holidays/restricted/${query}`, { withCredentials: true });
    return res.data;
  },

  getRestrictedSelections: async (year = '') => {
    const query = year ? `?year=${year}` : '';
    const res = await axios.get(`${BASE_URL}/api/leaves/holidays/restricted-selections/${query}`, { withCredentials: true });
    return res.data;
  },

  selectRestrictedHoliday: async (selectionData) => {
    const res = await axios.post(`${BASE_URL}/api/leaves/holidays/restricted-selections/`, selectionData, { withCredentials: true });
    return res.data;
  },

  cancelRestrictedHoliday: async (id) => {
    const res = await axios.post(`${BASE_URL}/api/leaves/holidays/restricted-selections/${id}/cancel/`, {}, { withCredentials: true });
    return res.data;
  },

  previewAccrual: async (periodDate) => {
    const res = await axios.post(
      `${BASE_URL}/api/leaves/accrual/`,
      { action: 'preview', period: periodDate, period_date: periodDate, dry_run: true },
      { withCredentials: true }
    );
    return res.data;
  },

  runAccrual: async (periodDate) => {
    const res = await axios.post(
      `${BASE_URL}/api/leaves/accrual/`,
      { action: 'run', period: periodDate, period_date: periodDate, dry_run: false },
      { withCredentials: true }
    );
    return res.data;
  },

  previewSandwich: async (previewData) => {
    const res = await axios.post(`${BASE_URL}/api/leaves/sandwich/preview/`, previewData, { withCredentials: true });
    return res.data;
  },

  getSandwichProposals: async (params = '') => {
    const query = params ? `?${params}` : '';
    const res = await axios.get(`${BASE_URL}/api/leaves/sandwich/proposals/${query}`, { withCredentials: true });
    return res.data;
  },

  applySandwichProposal: async (id) => {
    const res = await axios.post(`${BASE_URL}/api/leaves/sandwich/proposals/${id}/apply/`, {}, { withCredentials: true });
    return res.data;
  },

  getAbsenceCandidates: async (params = '') => {
    const query = params ? `?${params}` : '';
    const res = await axios.get(`${BASE_URL}/api/leaves/absences/candidates/${query}`, { withCredentials: true });
    return res.data;
  },

  scanAbsenceCandidates: async (targetDate) => {
    const res = await axios.post(`${BASE_URL}/api/leaves/absences/candidates/`, { target_date: targetDate }, { withCredentials: true });
    return res.data;
  },

  decideAbsenceCandidate: async (id, decision, notes = '') => {
    const res = await axios.post(
      `${BASE_URL}/api/leaves/absences/${id}/decide/`,
      { decision, hr_notes: notes, review_notes: notes },
      { withCredentials: true }
    );
    return res.data;
  },
};
